import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { addToCart } from "../../utils/cartUtils";
import { getImageUrl, getProfileImageUrl } from "../../utils/imageUtils";
import { formatFarmerLocation } from "../../utils/addressUtils";
import { toLocalizedNumber, toLocalizedPrice } from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import {
  FaStar,
  FaRegStar,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaEdit,
  FaShoppingCart,
  FaBolt,
  FaLeaf,
  FaMinus,
  FaPlus,
  FaTrash,
  FaChevronRight,
} from "react-icons/fa";
import "./ProductDetails.css";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast, showConfirm } = usePopup();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        /* ignore */
      }
    }
    fetchProductDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/products/${id}`);
      const d = response.data;
      const prod = {
        _id: d._id,
        name: d.name,
        price: d.price,
        unit: d.unit,
        category: d.category,
        image: d.images?.length > 0 ? getImageUrl(d.images[0]) : null,
        images: d.images?.map((img) => getImageUrl(img)) || [],
        description: d.description || "",
        farmer: d.farmer || {},
        stock:
          d.quantity > 0
            ? d.quantity <= 5
              ? "low-stock"
              : "in-stock"
            : "out-of-stock",
        stockQuantity: d.quantity || 0,
        isOrganic: d.isOrganic || false,
        minOrder: 1,
        maxOrder: d.quantity || 10,
        createdAt: d.createdAt || new Date(),
        rating: d.rating || 0,
        numReviews: d.numReviews || 0,
      };
      setProduct(prod);

      try {
        const reviewsRes = await api.get(`/reviews/product/${id}`);
        const revData = (reviewsRes.data || []).map((r) => ({
          ...r,
          consumer: r.consumer
            ? { ...r.consumer, username: r.consumer.username || r.consumer._id }
            : r.consumer,
        }));
        setReviews(revData);
        const userData = localStorage.getItem("user");
        if (userData) {
          try {
            const u = JSON.parse(userData);
            const existing = revData.find(
              (r) =>
                (r.consumer?._id || r.consumer)?.toString() ===
                (u.id || u._id)?.toString(),
            );
            if (existing) setUserReview(existing);
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        setReviews([]);
      }
      setLoading(false);
    } catch (e) {
      console.error("Error fetching product:", e);
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!user || user.role !== "consumer") {
      navigate("/login");
      return;
    }
    addToCart(product, quantity);
    showToast(
      t("productDetails.addedToCart", {
        qty: toLocalizedNumber(quantity),
        unit: product.unit,
        name: product.name,
      }),
      "success",
    );
  };

  const handleBuyNow = () => {
    if (!user || user.role !== "consumer") {
      navigate("/login");
      return;
    }
    navigate(`/checkout?product=${product._id}&quantity=${quantity}`);
  };

  const farmerId = product?.farmer?._id || product?.farmer?.id;
  const userId = user?._id || user?.id;
  const isOwnProduct = !!(
    user &&
    product &&
    user.role === "farmer" &&
    farmerId &&
    userId &&
    String(farmerId) === String(userId)
  );

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      showToast(t("productDetails.loginToReview"), "info");
      navigate("/login");
      return;
    }
    if (user.role !== "consumer") {
      showToast(t("productDetails.onlyConsumers"), "info");
      return;
    }
    if (!reviewForm.rating || reviewForm.rating < 1) {
      showToast(t("productDetails.selectRating"), "info");
      return;
    }
    setSubmittingReview(true);
    try {
      const response = await api.post("/reviews", {
        product: product._id,
        rating: reviewForm.rating,
        comment: reviewForm.comment.trim(),
      });
      const reviewsRes = await api.get(`/reviews/product/${id}`);
      setReviews(reviewsRes.data || []);
      setUserReview(response.data);
      setReviewForm({ rating: 5, comment: "" });
      setShowReviewForm(false);
      showToast(t("productDetails.reviewSuccess"), "success");
      fetchProductDetails();
    } catch (error) {
      showToast(
        error.response?.data?.message || t("productDetails.reviewFailed"),
        "error",
      );
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    showConfirm({
      title: t("productDetails.deleteReview"),
      message: t("productDetails.deleteReviewConfirm"),
      confirmLabel: t("common.delete"),
      onConfirm: async () => {
        try {
          await api.delete(`/reviews/${reviewId}`);
          const reviewsRes = await api.get(`/reviews/product/${id}`);
          setReviews(
            (reviewsRes.data || []).map((r) => ({
              ...r,
              consumer: r.consumer
                ? {
                    ...r.consumer,
                    username: r.consumer.username || r.consumer._id,
                  }
                : r.consumer,
            })),
          );
          setUserReview(null);
          showToast(t("productDetails.reviewDeleted"), "success");
          fetchProductDetails();
        } catch (e) {
          showToast(t("productDetails.reviewDeleteFailed"), "error");
        }
      },
    });
  };

  const renderStars = (rating, size = "sm") => (
    <div className={`pd-stars pd-stars--${size}`}>
      {[1, 2, 3, 4, 5].map((i) =>
        i <= Math.floor(rating) ? <FaStar key={i} /> : <FaRegStar key={i} />,
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="pd-page">
        <Header />
        <div className="pd-loading">
          <div className="pd-loading-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("productDetails.loading")}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pd-page">
        <Header />
        <div className="pd-empty">
          <h2>{t("productDetails.notFound")}</h2>
          <Link to="/market" className="pd-btn pd-btn--primary">
            {t("productDetails.backToGrocery")}
          </Link>
        </div>
      </div>
    );
  }

  const avgRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        ).toFixed(1)
      : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map((s) => ({
    star: s,
    count: reviews.filter((r) => r.rating === s).length,
  }));
  const maxRatingCount = Math.max(...ratingCounts.map((r) => r.count), 1);
  const allImages =
    product.images?.length > 0
      ? product.images
      : product.image
        ? [product.image]
        : [];

  return (
    <div className="pd-page">
      <Header />

      <div className="pd-container">
        {/* Breadcrumb */}
        <nav className="pd-breadcrumb">
          <Link to="/">{t("productDetails.home")}</Link>
          <FaChevronRight />
          <Link to="/market">{t("header.grocery")}</Link>
          <FaChevronRight />
          <Link to={`/market?category=${product.category}`}>
            {product.category}
          </Link>
          <FaChevronRight />
          <span>{product.name}</span>
        </nav>

        {/* Main Product Section */}
        <div className="pd-main">
          {/* Gallery */}
          <div className="pd-gallery">
            <div className="pd-gallery-main">
              {allImages.length > 0 ? (
                <img
                  src={allImages[selectedImage] || allImages[0]}
                  alt={product.name}
                  className="pd-gallery-img"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/deafultpic.png";
                  }}
                />
              ) : (
                <div className="pd-gallery-placeholder">
                  <img src="/images/deafultpic.png" alt={product.name} />
                </div>
              )}
              {product.isOrganic && (
                <span className="pd-organic-float">
                  <FaLeaf /> {t("common.organic")}
                </span>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="pd-gallery-thumbs">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    className={`pd-thumb ${selectedImage === i ? "pd-thumb--active" : ""}`}
                    onClick={() => setSelectedImage(i)}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${i + 1}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/deafultpic.png";
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="pd-info">
            <div className="pd-info-top">
              <span className="pd-category-tag">{product.category}</span>
              {product.isOrganic && (
                <span className="pd-organic-tag">
                  <FaLeaf /> {t("common.organic")}
                </span>
              )}
            </div>

            <h1 className="pd-title">{product.name}</h1>

            {/* Rating summary inline */}
            <div className="pd-rating-row">
              {renderStars(parseFloat(avgRating))}
              <span className="pd-rating-num">
                {toLocalizedNumber(avgRating)}
              </span>
              <span className="pd-rating-count">
                ({toLocalizedNumber(reviews.length)}{" "}
                {t("productDetails.reviews")})
              </span>
            </div>

            {/* Price */}
            <div className="pd-price-block">
              <span className="pd-price">
                Rs. {toLocalizedPrice(product.price)}
              </span>
              <span className="pd-price-unit">/ {product.unit}</span>
            </div>

            {/* Description */}
            {product.description && (
              <p className="pd-desc">{product.description}</p>
            )}

            {/* Farmer Card */}
            <Link
              to={`/users/${product.farmer.username || product.farmer._id}`}
              className="pd-farmer-card"
            >
              <img
                src={getProfileImageUrl(product.farmer?.profileImage)}
                alt={product.farmer.name}
                className="pd-farmer-avatar"
                referrerPolicy="no-referrer"
              />
              <div className="pd-farmer-info">
                <div className="pd-farmer-name-row">
                  <span className="pd-farmer-name">{product.farmer.name}</span>
                  {product.farmer.verified && (
                    <FaCheckCircle className="pd-farmer-verified" />
                  )}
                </div>
                <span className="pd-farmer-loc">
                  <FaMapMarkerAlt /> {formatFarmerLocation(product.farmer)}
                </span>
              </div>
              <FaChevronRight className="pd-farmer-arrow" />
            </Link>

            {/* Stock */}
            <div className="pd-stock-row">
              <span
                className={`pd-stock-badge pd-stock-badge--${product.stock}`}
              >
                {product.stock === "in-stock" ? (
                  <>
                    <FaCheckCircle /> {t("common.inStock")}
                  </>
                ) : product.stock === "low-stock" ? (
                  <>
                    <FaExclamationTriangle /> {t("common.lowStock")}
                  </>
                ) : (
                  <>
                    <FaTimes /> {t("common.outOfStock")}
                  </>
                )}
              </span>
              {product.stock !== "out-of-stock" && (
                <span className="pd-stock-qty">
                  {toLocalizedNumber(product.stockQuantity)} {product.unit}{" "}
                  available
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="pd-actions">
              {isOwnProduct ? (
                <Link
                  to={`/farmer/products/edit/${product._id}`}
                  className="pd-btn pd-btn--edit"
                >
                  <FaEdit /> {t("productDetails.editProduct")}
                </Link>
              ) : (
                <>
                  <div className="pd-qty-row">
                    <span className="pd-qty-label">
                      {t("productDetails.quantity")}
                    </span>
                    <div className="pd-qty-control">
                      <button
                        className="pd-qty-btn"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= product.minOrder}
                      >
                        <FaMinus />
                      </button>
                      <input
                        type="number"
                        className="pd-qty-input"
                        value={quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          setQuantity(
                            Math.max(
                              product.minOrder,
                              Math.min(product.maxOrder, val),
                            ),
                          );
                        }}
                        min={product.minOrder}
                        max={product.maxOrder}
                      />
                      <button
                        className="pd-qty-btn"
                        onClick={() =>
                          setQuantity(Math.min(product.maxOrder, quantity + 1))
                        }
                        disabled={quantity >= product.maxOrder}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <span className="pd-qty-unit">{product.unit}</span>
                  </div>

                  <div className="pd-btn-row">
                    <button
                      className="pd-btn pd-btn--cart"
                      onClick={handleAddToCart}
                      disabled={product.stock === "out-of-stock"}
                    >
                      <FaShoppingCart /> {t("productDetails.addToCart")}
                    </button>
                    <button
                      className="pd-btn pd-btn--buy"
                      onClick={handleBuyNow}
                      disabled={product.stock === "out-of-stock"}
                    >
                      <FaBolt /> {t("productDetails.buyNow")}
                    </button>
                  </div>

                  {!user && (
                    <p className="pd-login-hint">
                      <Link to="/login">{t("common.login")}</Link>{" "}
                      {t("productDetails.loginToAdd")}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Specs */}
        <div className="pd-section">
          <h2 className="pd-section-title">
            {t("productDetails.specifications")}
          </h2>
          <div className="pd-specs-grid">
            <div className="pd-spec">
              <span className="pd-spec-label">
                {t("productDetails.category")}
              </span>
              <span className="pd-spec-val">{product.category}</span>
            </div>
            <div className="pd-spec">
              <span className="pd-spec-label">{t("productDetails.unit")}</span>
              <span className="pd-spec-val">{product.unit}</span>
            </div>
            {product.isOrganic && (
              <div className="pd-spec">
                <span className="pd-spec-label">
                  {t("productDetails.certification")}
                </span>
                <span className="pd-spec-val pd-spec-val--green">
                  {t("productDetails.organicCertified")}
                </span>
              </div>
            )}
            <div className="pd-spec">
              <span className="pd-spec-label">
                {t("productDetails.minOrder")}
              </span>
              <span className="pd-spec-val">
                {toLocalizedNumber(product.minOrder)} {product.unit}
              </span>
            </div>
            <div className="pd-spec">
              <span className="pd-spec-label">
                {t("productDetails.maxOrder")}
              </span>
              <span className="pd-spec-val">
                {toLocalizedNumber(product.maxOrder)} {product.unit}
              </span>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="pd-section">
          <div className="pd-section-header">
            <h2 className="pd-section-title">
              {t("productDetails.customerReviews", {
                count: toLocalizedNumber(reviews.length),
              })}
            </h2>
            {user && user.role === "consumer" && !userReview && (
              <button
                className="pd-btn pd-btn--review-toggle"
                onClick={() => setShowReviewForm(!showReviewForm)}
              >
                {showReviewForm
                  ? t("common.cancel")
                  : t("productDetails.writeReview")}
              </button>
            )}
          </div>

          {/* Rating Summary */}
          {reviews.length > 0 && (
            <div className="pd-rating-summary">
              <div className="pd-rating-summary-left">
                <span className="pd-rating-big">{avgRating}</span>
                {renderStars(parseFloat(avgRating), "md")}
                <span className="pd-rating-total">
                  {toLocalizedNumber(reviews.length)}{" "}
                  {t("productDetails.reviews")}
                </span>
              </div>
              <div className="pd-rating-bars">
                {ratingCounts.map(({ star, count }) => (
                  <div key={star} className="pd-rating-bar-row">
                    <span className="pd-bar-label">
                      {toLocalizedNumber(star)} <FaStar />
                    </span>
                    <div className="pd-bar-track">
                      <div
                        className="pd-bar-fill"
                        style={{ width: `${(count / maxRatingCount) * 100}%` }}
                      />
                    </div>
                    <span className="pd-bar-count">
                      {toLocalizedNumber(count)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Review Form */}
          {showReviewForm &&
            user &&
            user.role === "consumer" &&
            !userReview && (
              <form className="pd-review-form" onSubmit={handleSubmitReview}>
                <div className="pd-review-form-group">
                  <label>{t("productDetails.ratingLabel")}</label>
                  <div className="pd-review-stars-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`pd-review-star-btn ${(hoverRating || reviewForm.rating) >= star ? "active" : ""}`}
                        onClick={() =>
                          setReviewForm({ ...reviewForm, rating: star })
                        }
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        {(hoverRating || reviewForm.rating) >= star ? (
                          <FaStar />
                        ) : (
                          <FaRegStar />
                        )}
                      </button>
                    ))}
                    <span className="pd-review-rating-text">
                      {toLocalizedNumber(reviewForm.rating)}/5
                    </span>
                  </div>
                </div>
                <div className="pd-review-form-group">
                  <label htmlFor="pd-review-comment">
                    {t("productDetails.yourReview")}
                  </label>
                  <textarea
                    id="pd-review-comment"
                    className="pd-review-textarea"
                    rows="4"
                    placeholder={t("productDetails.reviewPlaceholder")}
                    value={reviewForm.comment}
                    onChange={(e) =>
                      setReviewForm({ ...reviewForm, comment: e.target.value })
                    }
                    maxLength={500}
                  />
                  <span className="pd-review-char-count">
                    {reviewForm.comment.length}/500
                  </span>
                </div>
                <button
                  type="submit"
                  className="pd-btn pd-btn--primary"
                  disabled={submittingReview || !reviewForm.rating}
                >
                  {submittingReview
                    ? t("productDetails.submitting")
                    : t("productDetails.submitReview")}
                </button>
              </form>
            )}

          {/* Reviews List */}
          <div className="pd-reviews-list">
            {reviews.length === 0 ? (
              <div className="pd-reviews-empty">
                <FaStar className="pd-reviews-empty-icon" />
                <p>{t("productDetails.noReviews")}</p>
              </div>
            ) : (
              reviews.map((review) => {
                const consumer = review.consumer || review.user;
                const isUserReviewItem =
                  user &&
                  (consumer?._id || consumer)?.toString() ===
                    (user.id || user._id)?.toString();
                return (
                  <div key={review._id} className="pd-review-card">
                    <div className="pd-review-card-top">
                      <img
                        src={getProfileImageUrl(consumer?.profileImage)}
                        alt={consumer?.name || "User"}
                        className="pd-review-avatar"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/deafultpic.png";
                        }}
                      />
                      <div className="pd-review-meta">
                        {consumer?.username ? (
                          <Link
                            to={`/users/${consumer.username}`}
                            className="pd-review-name"
                          >
                            {consumer?.name || "Anonymous"}
                          </Link>
                        ) : (
                          <span className="pd-review-name">
                            {consumer?.name || "Anonymous"}
                          </span>
                        )}
                        <div className="pd-review-meta-bottom">
                          {renderStars(review.rating)}
                          <span className="pd-review-date">
                            {new Date(review.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="pd-review-card-right">
                        {review.isVerified && (
                          <span className="pd-verified-badge">
                            <FaCheckCircle />{" "}
                            {t("productDetails.verifiedPurchase")}
                          </span>
                        )}
                        {isUserReviewItem && (
                          <button
                            className="pd-review-delete-btn"
                            onClick={() => handleDeleteReview(review._id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="pd-review-comment">{review.comment}</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
